import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LuImages, LuMessagesSquare, LuShare2, LuSparkles, LuUsers } from 'react-icons/lu';
import { API_BASE_URL } from '../../config.js';
import { formatReportRange } from '../../utils/weekFormat.js';
import { Spinner } from '../ui/Spinner';
import { ErrorMessage } from '../ui/ErrorMessage';
import { Card } from '../ui/Card';
import Badge from '../ui/Badge';
import SegmentedControl from '../ui/SegmentedControl';
import ErrorBoundary from '../ui/ErrorBoundary';
import GlobeMark from '../ui/GlobeMark';
import { WeeklyGallery } from './WeeklyGallery';
import { AnalysesSection } from './AnalysesSection';
import { WeekTile } from './WeeklyReportList';
import { dumpFacts } from './dumpData.js';

/** Report opened from a public link: read-only analyses and gallery, no account needed. */
const PublicReport = () => {
  const { id, token } = useParams();
  const [report, setReport] = useState(null);
  const [client, setClient] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [dump, setDump] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('analyses');

  useEffect(() => {
    let cancelled = false;
    const fetchPublicReport = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/weekly/public/${id}/${token}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'Ce rapport n\'est pas disponible ou le lien a expiré.');
        if (cancelled) return;
        setReport(data.report);
        setClient(data.client);
        setAnalyses(data.analyses || []);
        setDump(data.dump);
      } catch (err) {
        console.error('Erreur lors du chargement du rapport public:', err);
        if (!cancelled) setError(err.message || 'Erreur lors du chargement du rapport');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPublicReport();
    return () => { cancelled = true; };
  }, [id, token]);

  const facts = useMemo(() => dumpFacts(dump, report, false), [dump, report]);

  if (loading) return <Spinner message="Chargement du rapport…" />;
  if (error) return <ErrorMessage message={error} title="Lien indisponible" showHomeLink={false} />;
  if (!report) return <ErrorMessage message="Rapport non trouvé" title="Lien indisponible" showHomeLink={false} />;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6 sm:px-6">
      <Card as="header">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
          <WeekTile week={report.week_number} large solid />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{client?.name || 'Rapport hebdomadaire'}</p>
              <Badge tone="violet" size="sm" icon={LuShare2}>Rapport partagé</Badge>
            </div>
            <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Semaine {report.week_number} · {report.year}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatReportRange(report, { long: true })}</p>
            {facts && (
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-600 dark:text-gray-300">
                <li className="inline-flex items-center gap-1.5"><LuMessagesSquare className="h-4 w-4 text-gray-400" aria-hidden /><strong className="font-semibold text-gray-900 dark:text-white">{facts.messages}</strong> messages</li>
                <li className="inline-flex items-center gap-1.5"><LuImages className="h-4 w-4 text-gray-400" aria-hidden /><strong className="font-semibold text-gray-900 dark:text-white">{facts.pictures}</strong> photos</li>
                <li className="inline-flex items-center gap-1.5"><LuUsers className="h-4 w-4 text-gray-400" aria-hidden /><strong className="font-semibold text-gray-900 dark:text-white">{facts.contributors}</strong> contributeurs</li>
              </ul>
            )}
          </div>
        </div>
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-900 sm:px-5">
          <SegmentedControl
            ariaLabel="Sections du rapport"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'analyses', label: 'Analyses', icon: LuSparkles, count: analyses.length },
              { value: 'gallery', label: 'Galerie', icon: LuImages, count: facts ? facts.pictures : null },
            ]}
          />
        </div>
      </Card>

      <ErrorBoundary resetKeys={[tab]}>
        {tab === 'analyses' ? (
          <AnalysesSection
            report={report}
            clientSlug={client?.slug}
            weekId={id}
            client={client}
            analyses={analyses}
            isAdminUser={false}
            isPublicView
          />
        ) : (
          <WeeklyGallery report={report} dump={dump} isAdminUser={false} clientSlug={client?.slug} weekId={id} />
        )}
      </ErrorBoundary>

      <footer className="flex items-center justify-center gap-2 py-4 text-xs text-gray-400 dark:text-gray-500">
        <GlobeMark className="h-4 w-4" />
        Rapport généré par Globe Weekly
      </footer>
    </div>
  );
};

export default PublicReport;
